import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, ArrowUp, Crown, Heart, Shield, AlertTriangle,
  BookOpen, Wrench, Theater, HelpCircle, History,
  Search, Film, Sparkles, Users, Zap, Scale,
  BarChart2, Activity, Info, ChevronDown, ChevronUp, ChevronRight,
  ExternalLink, Layers, Copy, Check, X, FileText
} from 'lucide-react';
import api from '../services/api';
import SexActionEditor from '../components/SexActionEditor';
import StatMeter from '../components/StatMeter';
import ContentWarning from '../components/ContentWarning';
import CrisisResources from '../components/CrisisResources';
import { to5Scale, to10Scale } from '../utils/displayConverters';
import { APPEAL_FACETS, getAppealProfileHighlights, describeAppealProfile } from '../utils/appealLenses';
import { useDevMode } from '../contexts/DevModeContext';
import { SlideUp, Spinner } from '../components/Animations';
import RatingWidget from '../components/RatingWidget';
import PerspectiveRatingWidget from '../components/PerspectiveRatingWidget';

// --- Configuration ---

const SECTION_CONFIG = {
  description: { icon: BookOpen, title: 'What It Is', color: 'text-blue-400' },
  appeal: { icon: Heart, title: 'Why It Works', color: 'text-wine-400' },
  for_dominants: { icon: Crown, title: 'For Givers', color: 'text-amber-400' },
  for_submissives: { icon: Users, title: 'For Receivers', color: 'text-pink-400' },
  experience_levels: { icon: Zap, title: 'Experience Levels', color: 'text-orange-400' },
  risks: { icon: Shield, title: 'Risks & Prevention', color: 'text-red-400' },
  accepted_risks: { icon: AlertTriangle, title: 'Risks You Accept', color: 'text-orange-500' },
  ethics: { icon: Scale, title: 'How to Talk About It', color: 'text-emerald-400' },
  physical_requirements: { icon: Activity, title: 'Physical Requirements', color: 'text-orange-400' },
  equipment: { icon: Wrench, title: 'Equipment & Gear', color: 'text-gray-400' },
  activities: { icon: Theater, title: 'Activities & Scenes', color: 'text-indigo-400' },
  imaginary_scenarios: { icon: Sparkles, title: 'Fantasy Scenarios', color: 'text-purple-400' },
  misconceptions: { icon: HelpCircle, title: 'Common Misconceptions', color: 'text-emerald-400' },
  glossary: { icon: BookOpen, title: 'Glossary', color: 'text-cyan-400' },
  history: { icon: History, title: 'History & Origins', color: 'text-amber-600' },
  resources: { icon: Search, title: 'Community & Resources', color: 'text-blue-300' },
  where_to_find: { icon: Film, title: 'Where to Find Content', color: 'text-pink-300' },
  media: { icon: Film, title: 'In Pop Culture', color: 'text-wine-300' },
  related_kinks: { icon: Layers, title: 'Related Variations', color: 'text-indigo-300' },
  // V2 sections
  community: { icon: Users, title: 'Community & Spaces', color: 'text-blue-400' },
  aftercare: { icon: Heart, title: 'Aftercare', color: 'text-pink-400' },
  combinations: { icon: Layers, title: 'Pairs Well With', color: 'text-purple-400' },
  progression: { icon: ChevronRight, title: "What's Next", color: 'text-indigo-400' },
  cultural_context: { icon: History, title: 'Cultural Context', color: 'text-amber-400' },
  solo_options: { icon: Users, title: 'Solo Play', color: 'text-pink-300' },
  porn_tags: { icon: Search, title: 'Porn Search Terms', color: 'text-wine-400' },
  coming_out: { icon: HelpCircle, title: 'Disclosure Guide', color: 'text-emerald-400' },
  red_flags: { icon: AlertTriangle, title: 'Red Flags', color: 'text-red-500' },
  // V3 Empathy Engine & Context sections
  empathy_simulation: { icon: Heart, title: 'The Empathy Engine', color: 'text-wine-500' },
  partner_dynamics: { icon: Users, title: 'Partner Dynamics', color: 'text-rose-400' },
  // Simulation sections
  simulation_10_levels: { icon: BarChart2, title: '10 Levels of Intensity', color: 'text-amber-400' },
  portrayal: { icon: Theater, title: 'How It Looks', color: 'text-indigo-400' },
  descent_10_level: { icon: BarChart2, title: 'The Descent (10 Levels)', color: 'text-amber-500' },
  // Technique & Learning sections
  technique_overview: { icon: Info, title: 'Technique Overview', color: 'text-blue-400' },
  beginner_tips: { icon: Zap, title: 'Beginner Tips', color: 'text-green-400' },
  advanced_techniques: { icon: Crown, title: 'Advanced Techniques', color: 'text-purple-400' },
  dirty_talk: { icon: Theater, title: 'Dirty Talk', color: 'text-pink-400' },
  partner_roles: { icon: Users, title: 'Partner Roles', color: 'text-rose-400' },
  // Safety sections
  safety_notes: { icon: Shield, title: 'Safety Notes', color: 'text-red-400' },
  long_term_impact: { icon: AlertTriangle, title: 'Long-Term Impact', color: 'text-orange-400' },
  // History & Psychology
  historical_precedent: { icon: History, title: 'Historical Precedent', color: 'text-amber-500' },
  psychological_aspects: { icon: Heart, title: 'Psychological Aspects', color: 'text-purple-500' },
  // Gear
  gear_required: { icon: Wrench, title: 'Gear Required', color: 'text-gray-400' },
  // Populator-generated sections
  scenarios_solo: { icon: Users, title: 'Solo Scenarios', color: 'text-pink-300' },
  scenarios_partnered: { icon: Users, title: 'Partnered Scenarios', color: 'text-rose-400' },
  risks_emotional: { icon: AlertTriangle, title: 'Emotional Risks', color: 'text-orange-400' },
  gear: { icon: Wrench, title: 'Gear & Equipment', color: 'text-gray-400' },
  preparation: { icon: Shield, title: 'Preparation', color: 'text-blue-400' },
  mechanics: { icon: Activity, title: 'Mechanics', color: 'text-indigo-400' },
  common_mistakes: { icon: AlertTriangle, title: 'Common Mistakes', color: 'text-orange-500' },
  social_stigma: { icon: HelpCircle, title: 'Social Stigma', color: 'text-amber-400' },
  roleplay_scripts: { icon: Theater, title: 'Roleplay Scripts', color: 'text-purple-400' },
  consent_guide: { icon: Scale, title: 'Consent Guide', color: 'text-emerald-400' },
  faq: { icon: HelpCircle, title: 'FAQ', color: 'text-cyan-400' },
  atmosphere: { icon: Sparkles, title: 'Atmosphere & Setting', color: 'text-indigo-300' },
  media_representation: { icon: Film, title: 'Media Representation', color: 'text-wine-300' },
  conclusion: { icon: BookOpen, title: 'Summary', color: 'text-gray-400' },
  dynamics: { icon: Users, title: 'Dynamics', color: 'text-rose-400' }
};

// Section categories for grouped display
const SECTION_CATEGORIES = [
  {
    id: 'understand',
    name: 'Understand It',
    icon: BookOpen,
    color: 'text-blue-400',
    sections: ['description', 'appeal', 'ethics', 'misconceptions', 'glossary', 'history', 'cultural_context', 'historical_precedent', 'psychological_aspects', 'social_stigma', 'faq', 'conclusion', 'dynamics']
  },
  {
    id: 'explore',
    name: 'Explore It',
    icon: Sparkles,
    color: 'text-purple-400',
    sections: ['activities', 'for_dominants', 'for_submissives', 'experience_levels', 'imaginary_scenarios', 'solo_options', 'combinations', 'progression', 'technique_overview', 'beginner_tips', 'advanced_techniques', 'dirty_talk', 'partner_roles', 'scenarios_solo', 'scenarios_partnered', 'roleplay_scripts', 'mechanics', 'preparation', 'atmosphere']
  },
  {
    id: 'safety',
    name: 'Stay Safe',
    icon: Shield,
    color: 'text-red-400',
    sections: ['risks', 'accepted_risks', 'physical_requirements', 'red_flags', 'aftercare', 'safety_notes', 'long_term_impact', 'risks_emotional', 'common_mistakes', 'consent_guide']
  },
  {
    id: 'connect',
    name: 'Connect',
    icon: Users,
    color: 'text-pink-400',
    sections: ['partner_dynamics', 'community', 'resources', 'coming_out']
  },
  {
    id: 'discover',
    name: 'Discover More',
    icon: Film,
    color: 'text-wine-400',
    sections: ['where_to_find', 'media', 'media_representation', 'porn_tags', 'related_kinks', 'related', 'equipment', 'gear_required', 'gear']
  },
  {
    id: 'simulation',
    name: 'Simulation',
    icon: Zap,
    color: 'text-amber-400',
    sections: ['simulation_10_levels', 'empathy_simulation', 'portrayal', 'descent_10_level']
  }
];

const SPECIAL_KEYS = ['description'];

// Helper: prefer formatted content when available
const getDisplayContent = (section) => section?.contentFormatted || section?.content || '';

// Helper: Scroll to section with proper offset
const scrollToSection = (sectionId, container) => {
  const el = document.getElementById(sectionId);
  if (el && container) {
    const offset = el.offsetTop - 88;
    container.scrollTo({ top: offset, behavior: 'smooth' });
  }
};

// --- Components ---

// Compact stat for mobile - with visible track and percentage
const MiniStat = ({ label, value, scale = 10 }) => {
  // Value is already normalized (e.g. 4.5 or 3)
  const numValue = Number(value);
  const percentage = Math.min(100, Math.max(0, (numValue / scale) * 100));

  return (
    <div className="text-center" role="meter" aria-label={label} aria-valuenow={numValue} aria-valuemin="0" aria-valuemax={scale}>
      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1" aria-hidden="true">{label}</div>
      <div className="relative h-2 w-full bg-dark-700 rounded-full overflow-hidden border border-dark-600" aria-hidden="true">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-wine-600 to-amber-500 rounded-full" style={{ width: `${percentage}%` }} />
      </div>
      <div className="text-xs font-bold text-gray-300 mt-1" aria-hidden="true">{value}/{scale}</div>
    </div>
  );
};


const SectionCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`glass-card p-6 md:p-8 border border-wine-800/30 ${className}`}>
    <div className="flex items-center gap-3 mb-6 border-b border-wine-700/30 pb-4">
      {Icon && <Icon className="w-6 h-6 text-[#C4A35A]" />}
      <h3 className="text-2xl font-fantasy font-semibold text-cream/90 tracking-wide">{title}</h3>
    </div>
    <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-p:text-cream/70 prose-strong:text-cream prose-li:text-cream/70 prose-table:border-collapse prose-th:bg-wine-950/50 prose-th:text-cream/90 prose-th:p-3 prose-td:p-3 prose-td:border-wine-700/30 max-w-none leading-relaxed overflow-x-auto">
      {children}
    </div>
  </div>
);

// Accordion with copy link button
const AccordionItem = ({ sectionKey, content, isOpen, onToggle, isActive, onCopyLink }) => {
  const config = SECTION_CONFIG[sectionKey] || { title: sectionKey, icon: HelpCircle, color: 'text-gray-400' };
  const Icon = config.icon;
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#${sectionKey}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass-card overflow-hidden transition-all duration-300 border border-wine-800/30 hover:border-wine-700/40">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${sectionKey}`}
        className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors min-h-[56px] focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/30 focus:ring-inset"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${config.color}`} aria-hidden="true" />
          <h3 className="font-fantasy font-semibold text-cream/90 text-lg m-0 tracking-wide">{config.title}</h3>
        </div>
        <div className="flex items-center gap-1">
          {isOpen ? <ChevronUp className="w-5 h-5 text-cream/40" aria-hidden="true" /> : <ChevronDown className="w-5 h-5 text-cream/40" aria-hidden="true" />}
        </div>
      </button>

      {isOpen && (
        <div id={`accordion-content-${sectionKey}`} className="px-5 pb-6 pt-2 border-t border-wine-700/30" data-accordion-content>
          {/* Copy link button moved inside, after content */}
          <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-p:text-cream/70 prose-strong:text-cream prose-li:text-cream/70 prose-table:border-collapse prose-th:bg-wine-950/50 prose-th:text-cream/90 prose-th:p-3 prose-td:p-3 prose-td:border-wine-700/30 max-w-none leading-relaxed overflow-x-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
          <div className="mt-4 pt-3 border-t border-wine-700/20 flex justify-end">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-cream/40 hover:text-[#C4A35A] hover:bg-wine-900/30 rounded-md transition-colors"
            >
              {copied ? <><Check className="w-3.5 h-3.5 text-teal-400" aria-hidden="true" /> Copied!</> : <><Copy className="w-3.5 h-3.5" aria-hidden="true" /> Copy Link</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Floating back to top button
const BackToTopButton = ({ show, onClick }) => (
  <button
    onClick={onClick}
    className={`fixed bottom-6 right-6 z-50 p-4 rounded-full bg-wine-600 hover:bg-wine-500 text-white shadow-2xl transition-all duration-300 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    aria-label="Back to top"
  >
    <ArrowUp className="w-5 h-5" />
  </button>
);

// Mobile floating TOC button
const MobileTOCButton = ({ show, onClick }) => (
  <button
    onClick={onClick}
    className={`fixed bottom-6 left-6 z-50 lg:hidden p-3 rounded-full bg-dark-800 border border-red-500/30 text-white shadow-xl transition-all duration-300 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    aria-label="Jump to section"
  >
    <Layers className="w-5 h-5 text-wine-400" />
  </button>
);

// Mobile TOC Drawer
const MobileTOCDrawer = ({ isOpen, onClose, sections, onSelect }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <SlideUp className="absolute bottom-0 left-0 right-0 max-h-[70vh] bg-dark-900 rounded-t-2xl border-t border-wine-500/30 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <h3 className="font-bold text-white">Jump To Section</h3>
          <button onClick={onClose} className="p-2 hover:bg-dark-700 rounded-lg" aria-label="Close modal">
            <X className="w-5 h-5 text-gray-400" aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-4">
          {SECTION_CATEGORIES.map(category => {
            const categorySections = category.sections.filter(
              key => sections[key] && sections[key].content && !SPECIAL_KEYS.includes(key)
            );
            if (categorySections.length === 0) return null;

            const CategoryIcon = category.icon;

            return (
              <div key={category.id}>
                <div className="flex items-center gap-2 px-2 py-1 mb-2">
                  <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{category.name}</span>
                </div>
                <div className="space-y-1">
                  {categorySections.map(key => {
                    const config = SECTION_CONFIG[key];
                    const Icon = config?.icon || HelpCircle;
                    return (
                      <button
                        key={key}
                        onClick={() => onSelect(key)}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-dark-800/50 hover:bg-dark-700 text-left transition-colors"
                      >
                        <Icon className={`w-4 h-4 ${config?.color || 'text-gray-400'}`} />
                        <span className="text-gray-200">{config?.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </SlideUp>
    </div>
  );
};

// --- Main Page Component ---

const KinkPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const { isDevMode } = useDevMode();

  const [kink, setKink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileTOCOpen, setMobileTOCOpen] = useState(false);
  const [showActionEditor, setShowActionEditor] = useState(false);

  // Handle scroll for back-to-top button and progress
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

    setShowBackToTop(scrollTop > 400);
    setScrollProgress(progress);
  }, []);

  // Load kink data
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = 0;
      container.addEventListener('scroll', handleScroll);
    }
    loadKink();
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [id, handleScroll]);

  // SEO: Dynamic page title
  useEffect(() => {
    if (kink?.name) {
      document.title = `${kink.name} — Pleasance`;
    }
    return () => { document.title = 'Pleasance — Play Without Shame'; };
  }, [kink]);

  // Handle deep links (hash navigation)
  useEffect(() => {
    if (!kink || !location.hash) return;
    const sectionKey = location.hash.slice(1);
    // Check if this section exists in the kink's sections
    if (kink.sections && kink.sections[sectionKey]) {
      setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));
      setTimeout(() => {
        scrollToSection(`section-${sectionKey}`, containerRef.current);
      }, 100);
    }
  }, [kink, location.hash]);

  // Intersection Observer for active section - uses scroll container as root
  useEffect(() => {
    if (!kink || !containerRef.current) return;

    const container = containerRef.current;

    // Track all visible sections and their positions
    const visibleSections = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const sectionId = entry.target.id.replace('section-', '');
          if (entry.isIntersecting) {
            // Store the top position relative to container
            visibleSections.set(sectionId, entry.boundingClientRect.top);
          } else {
            visibleSections.delete(sectionId);
          }
        });

        // Find the first visible section (closest to top)
        if (visibleSections.size > 0) {
          let bestSection = null;
          let bestTop = Infinity;

          visibleSections.forEach((top, id) => {
            if (top < bestTop) {
              bestTop = top;
              bestSection = id;
            }
          });

          if (bestSection) {
            setActiveSection(bestSection);
          }
        }
      },
      {
        root: container, // Use the scroll container as root!
        threshold: 0,
        rootMargin: '-100px 0px -70% 0px' // Top 30% of visible area triggers
      }
    );

    // Observe all sections - find them dynamically from the DOM
    if (kink.sections) {
      Object.keys(kink.sections).forEach(key => {
        const el = document.getElementById(`section-${key}`);
        if (el) observer.observe(el);
      });
    }

    return () => observer.disconnect();
  }, [kink]);



  const loadKink = async () => {
    try {
      setLoading(true);
      const data = await api.getKink(id);
      setKink(data);
    } catch (err) {
      console.error('Failed to load kink:', err);
      setError('Failed to load kink details');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleBackToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-dark-950">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !kink) {
    return (
      <div className="h-full flex flex-col items-center justify-center space-y-4 bg-dark-950">
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error || 'Kink not found'}</span>
        </div>
        <button onClick={() => navigate('/')} className="btn-secondary flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Go Back
        </button>
      </div>
    );
  }

  const sections = kink.sections || {};
  const availableKeys = Object.keys(sections).filter(key => sections[key] && sections[key].content);
  const accordionKeys = availableKeys.filter(key => !SPECIAL_KEYS.includes(key));
  const {
    primary: primaryAppealLens,
    supporting: supportingAppealLenses,
    notable: notableAppealLenses
  } = getAppealProfileHighlights(kink);
  const appealSummary = describeAppealProfile(kink);

  return (
    <div ref={containerRef} id="kink-page-scroll-container" className="h-full overflow-y-auto bg-dark-950 relative custom-scrollbar">

      {/* Reading Progress Bar - thicker for better visibility */}
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-dark-800/80 z-50 backdrop-blur-sm">
        <div
          className="h-full bg-gradient-to-r from-wine-500 via-amber-500 to-wine-500 transition-all duration-150 shadow-lg shadow-wine-500/20"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Hero Section - Fantasy Theme */}
      <div className="relative border-b border-wine-700/30 pt-10 pb-16 mb-10 backdrop-blur-sm overflow-hidden">
        {/* Ambient glow effect - burgundy and gold */}
        <div className="absolute inset-0 bg-gradient-to-br from-wine-950/40 via-[#0A0A0A]/80 to-[#0A0A0A] pointer-events-none" />
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-wine-700/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm mb-6">
            <Link to="/" className="text-gray-400 hover:text-wine-400 transition-colors font-medium">Browse</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <Link to="/?tab=kinks" className="text-gray-400 hover:text-wine-400 transition-colors font-medium">Kinks</Link>
            {kink.parentKink && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-600" />
                <Link to={`/kink/${kink.parentKink.id}`} className="text-gray-400 hover:text-wine-400 transition-colors font-medium">{kink.parentKink.name}</Link>
              </>
            )}
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-white font-medium">{kink.name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-8">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/?category=${encodeURIComponent(kink.category || 'Uncategorized')}`}
                  className="px-3 py-1 rounded text-xs font-bold uppercase tracking-[0.15em] bg-wine-950/50 text-[#C4A35A]/80 border border-wine-700/40 hover:border-[#C4A35A]/50 hover:text-[#C4A35A] transition-colors"
                >
                  {kink.category || 'Uncategorized'}
                </Link>
              </div>

              <h1 className="text-5xl md:text-6xl font-fantasy font-semibold text-cream/95 mb-6 tracking-wide leading-tight">
                {kink.name}
              </h1>

              {/* Gold ornate divider */}
              <div className="ornate-divider w-32 mb-6" />

              {/* Content Warning - shown for edgeplay/intense content */}
              {(kink.sections?.accepted_risks?.content || kink.ethicsRating > 7) && (
                <ContentWarning
                  type={kink.ethicsRating > 8 ? 'edgeplay' : 'intense'}
                  className="mb-6 max-w-2xl"
                />
              )}

              <p className="text-2xl text-gray-300 leading-relaxed max-w-3xl font-light">
                {kink.summary || 'No summary available.'}
              </p>
            </div>

            <div className="flex flex-col items-end gap-3 mt-4 md:mt-0">
              <div className="flex gap-2">
                <Link to="/?focus=search" className="btn-secondary flex items-center gap-2 group px-4 py-3 text-sm">
                  <Search className="w-4 h-4" />
                  Search
                </Link>
                <button onClick={() => navigate('/')} className="btn-secondary flex items-center gap-2 group px-4 py-3 text-sm">
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  Browse
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rating Widget - Right below hero */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-6 mb-10 relative z-10">
        <div className="glass-card p-4 md:p-6 border border-wine-700/30">
          <div className="text-sm uppercase tracking-wider text-cream/60 font-medium mb-4">Rate This Kink</div>
          <PerspectiveRatingWidget
            entityType="kink"
            entityId={kink.id}
            variant="buttons"
            size="lg"
          />
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Left Column: Main Content */}
          <div className="lg:col-span-2 space-y-10">

            {/* OVERVIEW CARD */}
            {sections.description && (
              <div id="section-overview" className="glass-card p-6 md:p-8 border-l-4 border-wine-500">
                <div className="flex items-center gap-3 mb-6 border-b border-dark-700/50 pb-4">
                  <BookOpen className="w-7 h-7 text-wine-500" />
                  <h3 className="text-2xl font-display font-bold text-gray-100">Overview</h3>
                </div>

                {/* TL;DR Content */}
                <div className="prose prose-lg prose-invert text-gray-200 prose-headings:text-gray-100 prose-strong:text-white prose-table:border-collapse prose-th:bg-dark-800 prose-th:p-3 prose-td:p-3 max-w-none leading-relaxed overflow-x-auto mb-6">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{getDisplayContent(sections.description)}</ReactMarkdown>
                </div>

                {/* Mobile-only compact stats */}
                <div className="lg:hidden pt-4 border-t border-dark-700/50">
                  <h4 className="text-sm font-bold text-gray-400 mb-3">At a Glance</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MiniStat label="Intensity" value={to5Scale(kink.weirdnessScore)} scale={5} />
                    <MiniStat label="Realism" value={to10Scale(kink.realism)} scale={10} />
                    <MiniStat label="Ethics" value={to10Scale(kink.ethics)} scale={10} />
                    <MiniStat label="Rarity" value={to10Scale(kink.rarity)} scale={10} />
                  </div>
                  <div className="mt-4 pt-4 border-t border-dark-700/50">
                    <h4 className="text-sm font-bold text-gray-400 mb-3">Appeals</h4>
                    <div className="flex flex-wrap gap-2">
                      {[primaryAppealLens, ...supportingAppealLenses, ...(!primaryAppealLens ? notableAppealLenses : [])]
                        .filter((facet, index, arr) => facet && arr.findIndex(item => item?.key === facet.key) === index)
                        .map((facet) => (
                          <span key={facet.key} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider ${facet.chipClass}`}>
                            {facet.label}
                          </span>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Mobile-only quick links */}
                <div className="pt-4 border-t border-dark-700/50 lg:hidden mt-4">
                  <h4 className="text-sm font-bold text-gray-400 mb-3">Quick Navigation</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {accordionKeys.slice(0, 6).map(key => {
                      const config = SECTION_CONFIG[key];
                      const Icon = config?.icon || HelpCircle;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            scrollToSection(`section-${key}`, containerRef.current);
                            setExpandedSections(prev => ({ ...prev, [key]: true }));
                          }}
                          className="flex items-center gap-2 px-3 py-3 rounded-md bg-dark-800/50 border border-dark-700/50 text-sm text-gray-300 hover:text-white hover:bg-dark-700/50 min-h-[48px]"
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="break-words">{config?.title || key}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Complete Guide Header - Matching SexActionPage */}
            <h2 className="text-xl font-display font-bold text-white flex items-center gap-3 mb-4">
              <BookOpen className="w-5 h-5 text-wine-400" />
              Complete Guide
            </h2>

            {/* Expand/Collapse All Controls */}
            <div id="main-content" className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">{accordionKeys.length} sections available</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const allExpanded = {};
                    accordionKeys.forEach(key => allExpanded[key] = true);
                    setExpandedSections(allExpanded);
                  }}
                  className="text-xs px-3 py-1.5 rounded-md bg-dark-800 hover:bg-dark-700 text-gray-400 hover:text-white border border-dark-700 transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={() => setExpandedSections({})}
                  className="text-xs px-3 py-1.5 rounded-md bg-dark-800 hover:bg-dark-700 text-gray-400 hover:text-white border border-dark-700 transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {/* Dynamic Content Sections - grouped by category */}
            <div className="space-y-8">
              {SECTION_CATEGORIES.map(category => {
                // Get sections that belong to this category AND exist in the data
                const categorySections = category.sections.filter(
                  key => sections[key] && sections[key].content && !SPECIAL_KEYS.includes(key)
                );

                if (categorySections.length === 0) return null;

                const CategoryIcon = category.icon;

                return (
                  <div key={category.id}>
                    <div className="flex items-center gap-2 mb-4 px-2">
                      <CategoryIcon className={`w-5 h-5 ${category.color}`} />
                      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">
                        {category.name}
                      </h3>
                      <div className="flex-1 h-px bg-gradient-to-r from-dark-700 to-transparent ml-2" />
                    </div>
                    <div className="space-y-3">
                      {categorySections.map(key => (
                        <div key={key} id={`section-${key}`}>
                          <AccordionItem
                            sectionKey={key}
                            content={getDisplayContent(sections[key])}
                            isOpen={expandedSections[key]}
                            onToggle={() => toggleSection(key)}
                            isActive={activeSection === key}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Uncategorized sections fallback */}
              {Object.keys(sections)
                .filter(key => {
                  const isInCategory = SECTION_CATEGORIES.some(cat => cat.sections.includes(key));
                  return sections[key] && sections[key].content && !SPECIAL_KEYS.includes(key) && !isInCategory;
                })
                .length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4 px-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">
                        Other
                      </h3>
                      <div className="flex-1 h-px bg-gradient-to-r from-dark-700 to-transparent ml-2" />
                    </div>
                    <div className="space-y-3">
                      {Object.keys(sections)
                        .filter(key => {
                          const isInCategory = SECTION_CATEGORIES.some(cat => cat.sections.includes(key));
                          return sections[key] && sections[key].content && !SPECIAL_KEYS.includes(key) && !isInCategory;
                        })
                        .map(key => (
                          <div key={key} id={`section-${key}`}>
                            <AccordionItem
                              sectionKey={key}
                              content={getDisplayContent(sections[key])}
                              isOpen={expandedSections[key]}
                              onToggle={() => toggleSection(key)}
                              isActive={activeSection === key}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Variations / Child Kinks */}
            {kink.childKinks && kink.childKinks.length > 0 && (
              <div className="glass-card p-8 border-t-2 border-t-wine-500/30">
                <h3 className="text-xl font-display font-bold text-white mb-6 flex items-center gap-3">
                  <Layers className="w-6 h-6 text-wine-500" />
                  Ways People Explore {kink.name}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {kink.childKinks.map(child => (
                    <Link
                      key={child.id}
                      to={`/kink/${child.id}`}
                      className="block p-4 rounded-xl bg-dark-800/50 hover:bg-dark-800 border border-dark-700 hover:border-red-500/30 transition-all group"
                    >
                      <div className="font-bold text-wine-300 group-hover:text-wine-200 mb-1">{child.name}</div>
                      {child.summary && <div className="text-sm text-gray-400 line-clamp-2">{child.summary}</div>}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* WHO LIKES THIS? - Archetype Affinities */}
            {kink.archetypes && kink.archetypes.length > 0 && (
              <div className="glass-card p-8 border-t-2 border-t-accent-500/30">
                <h3 className="text-xl font-display font-bold text-white mb-6 flex items-center gap-3">
                  <Crown className="w-6 h-6 text-accent-500" />
                  Who This Tends to Click For
                </h3>

                {/* Core Desires */}
                {kink.archetypes.some(a => a.ArchetypeKink?.interest_level === 'CORE') && (
                  <div className="mb-6">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-accent-400 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-accent-500"></span> Strong Match
                      </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {kink.archetypes.filter(a => a.ArchetypeKink?.interest_level === 'CORE').map(archetype => (
                        <Link key={archetype.id} to={`/archetype/${archetype.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-dark-800/50 hover:bg-dark-800 border border-transparent hover:border-accent-500/30 transition-all group">
                          <span className="text-2xl">{archetype.icon}</span>
                          <div>
                            <div className="font-bold text-gray-200 group-hover:text-accent-300">{archetype.name}</div>
                            {archetype.ArchetypeKink?.reason && <div className="text-xs text-gray-500 line-clamp-1">"{archetype.ArchetypeKink.reason}"</div>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Joy & Curiosity */}
                {kink.archetypes.some(a => ['JOY', 'CURIOUS'].includes(a.ArchetypeKink?.interest_level)) && (
                  <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span> Likely Curiosity
                      </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {kink.archetypes.filter(a => ['JOY', 'CURIOUS'].includes(a.ArchetypeKink?.interest_level)).map(archetype => (
                        <Link key={archetype.id} to={`/archetype/${archetype.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-dark-800/50 hover:bg-dark-800 border border-transparent hover:border-blue-500/30 transition-all group">
                          <span className="text-2xl">{archetype.icon}</span>
                          <div>
                            <div className="font-bold text-gray-200 group-hover:text-blue-300">{archetype.name}</div>
                            <div className="text-xs text-gray-500 px-1.5 py-0.5 rounded bg-dark-900/50 inline-block mt-1">
                              {archetype.ArchetypeKink?.interest_level}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* You Might Also Enjoy - Behavioral Overlaps */}
            {kink.overlappingKinks && kink.overlappingKinks.length > 0 && (
              <div className="mt-8 glass-card p-6 border-l-4 border-l-purple-500/50">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-purple-400" />
                  You Might Also Enjoy
                </h3>
                <p className="text-sm text-gray-400 mb-6">Based on psychological patterns and shared interests, people who enjoy {kink.name} often also explore these kinks.</p>

                <div className="space-y-4">
                  {kink.overlappingKinks.map(overlap => {
                    // Convert connection strength (1-10) to percentage (40-95%)
                    const overlapPercent = Math.min(95, Math.max(40, (overlap.connectionStrength || 7) * 10 + 25));

                    return (
                      <Link
                        key={overlap.id}
                        to={`/kink/${overlap.id}`}
                        className="block p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-dark-800/50 hover:from-purple-500/20 hover:to-dark-700/50 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
                      >
                        <div className="flex items-start gap-4">
                          {/* Overlap Percentage Circle */}
                          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-purple-500/20 border-2 border-purple-500/50 flex items-center justify-center">
                            <span className="text-lg font-bold text-purple-300">{overlapPercent}%</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-lg text-purple-200 group-hover:text-white">{overlap.name}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{overlap.category || 'Other'}</span>
                            </div>

                            {/* Why This Overlaps */}
                            {overlap.connectionReason && (
                              <div className="mt-2 p-3 rounded-lg bg-dark-900/50 border-l-2 border-purple-400/50">
                                <div className="text-xs font-semibold uppercase tracking-wider text-purple-400 mb-1">Why the overlap?</div>
                                <div className="text-sm text-gray-300">{overlap.connectionReason}</div>
                              </div>
                            )}
                          </div>

                          {/* Arrow */}
                          <div className="flex-shrink-0 text-purple-400/50 group-hover:text-purple-300 transition-colors">
                            <ChevronDown className="w-5 h-5 -rotate-90" />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Creative Discovery Section */}
            <div className="mt-8 space-y-6">
              {/* Exploration Prompts Based on Kink */}
              <div className="glass-card p-6 bg-gradient-to-br from-dark-900 via-dark-800/80 to-dark-900 border-l-4 border-l-wine-500/50">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  What's Next?
                </h3>
                <div className="space-y-3 text-sm">
                  {kink.weirdnessScore > 60 && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-dark-800/50 border border-dark-700/50">
                      <span className="text-xl">🔥</span>
                      <div>
                        <div className="font-medium text-gray-200">Ready for intensity?</div>
                        <div className="text-gray-400">You might also enjoy exploring darker power dynamics or edge play. Start with proper safety research first.</div>
                      </div>
                    </div>
                  )}
                  {kink.weirdnessScore <= 40 && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-dark-800/50 border border-dark-700/50">
                      <span className="text-xl">🌱</span>
                      <div>
                        <div className="font-medium text-gray-200">Perfect for beginners</div>
                        <div className="text-gray-400">This is a great entry point. Consider exploring similar sensory-focused activities as you build experience.</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-dark-800/50 border border-dark-700/50">
                    <span className="text-xl">💬</span>
                    <div>
                      <div className="font-medium text-gray-200">Talk to your partner</div>
                      <div className="text-gray-400">Use the scenarios and techniques above as conversation starters. The best kink experiences start with communication.</div>
                    </div>
                  </div>
                  {kink.category && (
                    <Link
                      to={`/?category=${encodeURIComponent(kink.category)}`}
                      className="flex items-start gap-3 p-3 rounded-lg bg-wine-500/10 border border-wine-500/20 hover:bg-wine-500/15 transition-colors"
                    >
                      <span className="text-xl">🎯</span>
                      <div>
                        <div className="font-medium text-wine-300">Explore more {kink.category} kinks</div>
                        <div className="text-gray-400">Discover other activities in this category that might appeal to you.</div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-6 rounded-xl bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 border border-dark-700/50 text-center">
                <p className="text-gray-400 mb-4">Ready to discover something new?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-wine-600 hover:bg-wine-500 text-white font-medium transition-all shadow-lg shadow-wine-500/20"
                  >
                    <Search className="w-4 h-4" />
                    Browse All Kinks
                  </Link>
                  <button
                    onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-dark-700 hover:bg-dark-600 text-gray-300 font-medium transition-all border border-dark-600"
                  >
                    <ArrowUp className="w-4 h-4" />
                    Back to Top
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Sidebar - Sticky */}
          <div className="lg:col-span-1 space-y-8">
            <div className="sticky top-8 space-y-6 max-h-[calc(100vh-4rem)] overflow-y-auto no-scrollbar">

              {/* Back to Index Button */}
              <button
                onClick={() => scrollToSection('section-overview', containerRef.current)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-dark-800/80 hover:bg-dark-700 border border-dark-700 hover:border-wine-500/30 transition-all text-gray-300 hover:text-white group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-medium">Back to Index</span>
              </button>

              {/* Quick Index - Desktop Only - Grouped by Category */}
              <div className="hidden lg:block glass-card p-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4">Jump To</h4>
                <div className="space-y-4">
                  {SECTION_CATEGORIES.map(category => {
                    const categorySections = category.sections.filter(
                      key => sections[key] && sections[key].content && !SPECIAL_KEYS.includes(key)
                    );
                    if (categorySections.length === 0) return null;

                    const CategoryIcon = category.icon;

                    return (
                      <div key={category.id}>
                        <div className="flex items-center gap-1.5 px-2 py-1 mb-1">
                          <CategoryIcon className={`w-3 h-3 ${category.color}`} />
                          <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{category.name}</span>
                        </div>
                        <div className="space-y-0.5">
                          {categorySections.map(key => {
                            const config = SECTION_CONFIG[key];
                            const Icon = config?.icon || HelpCircle;
                            const isActive = activeSection === key;
                            return (
                              <button
                                key={key}
                                onClick={() => {
                                  scrollToSection(`section-${key}`, containerRef.current);
                                  setExpandedSections(prev => ({ ...prev, [key]: true }));
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left text-sm transition-all ${isActive
                                  ? 'bg-wine-500/20 text-white border-l-2 border-wine-500'
                                  : 'hover:bg-dark-700/50 text-gray-400 hover:text-white'
                                  }`}
                              >
                                <Icon className={`w-3 h-3 ${isActive ? 'text-wine-400' : config?.color || 'text-gray-400'}`} />
                                <span className="break-words text-sm">{config?.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Core Actions - New Section */}
              <div className="glass-card p-6 border-l-4 border-l-yellow-500/50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-yellow-500" />
                    Core Actions
                  </h3>
                  {isDevMode && (
                    <button onClick={() => setShowActionEditor(true)} className="text-xs text-dark-400 hover:text-yellow-500 transition-colors uppercase font-bold tracking-wider">
                      Edit
                    </button>
                  )}
                </div>

                {kink.sexActions && kink.sexActions.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {kink.sexActions.map(action => (
                      <Link
                        key={action.id}
                        to={`/action/${action.id}`}
                        className="flex items-start gap-3 p-3 rounded-lg bg-dark-800/50 hover:bg-dark-700 border border-dark-700 hover:border-yellow-500/30 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-full bg-dark-900 border border-dark-700 flex items-center justify-center text-xs font-mono text-yellow-500/80 shrink-0">
                          {action.intensity || '-'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-200 group-hover:text-white text-sm">{action.name}</span>
                            {action.category && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-dark-700 text-dark-400 rounded">{action.category}</span>
                            )}
                          </div>
                          {action.description && (
                            <p className="text-xs text-dark-500 mt-0.5 line-clamp-2">{action.description}</p>
                          )}
                          {action.KinkSexAction?.note && (
                            <div className="text-xs text-yellow-500/60 leading-none mt-1 italic">✦ {action.KinkSexAction.note}</div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : isDevMode ? (
                  <button
                    onClick={() => setShowActionEditor(true)}
                    className="w-full py-6 text-sm text-dark-500 hover:text-dark-300 border border-dashed border-dark-700 hover:border-dark-600 rounded-lg transition-colors flex flex-col items-center gap-2"
                  >
                    <Activity className="w-5 h-5 opacity-50" />
                    <span>Link Sex Actions</span>
                  </button>
                ) : (
                  <div className="w-full py-6 text-sm text-dark-500 text-center">
                    No actions linked yet.
                  </div>
                )}
              </div>

              {/* Stats Card - Desktop Only */}
              <div className="hidden lg:block glass-card p-6 space-y-6 border-t-4 border-t-dark-700">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-2">
                  <BarChart2 className="w-4 h-4" />
                  At a Glance
                </h3>

                <div className="space-y-5">
                  <StatMeter label="Intensity" value={to5Scale(kink.weirdnessScore)} scale={5} colorClass="bg-gradient-to-r from-wine-500 to-amber-500" />
                  <StatMeter label="Realism" value={to10Scale(kink.realism)} scale={10} colorClass="bg-wine-500" />
                  <StatMeter label="Ethics" value={to10Scale(kink.ethics)} scale={10} colorClass={kink.ethics < 80 ? "bg-wine-600" : "bg-wine-400"} />
                  <StatMeter label="Rarity" value={to10Scale(kink.rarity)} scale={10} colorClass="bg-wine-500" />
                </div>

                <div className="pt-6 border-t border-dark-700/50 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500">Appeals</h4>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {[primaryAppealLens, ...supportingAppealLenses, ...notableAppealLenses]
                      .filter((facet, index, arr) => facet && arr.findIndex(item => item?.key === facet.key) === index)
                      .map((facet) => (
                        <span key={facet.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${facet.chipClass}`}>
                          {facet.label}
                        </span>
                      ))}
                  </div>
                </div>
              </div>

              {/* Related Kinks Discovery Cards */}
              {kink.relatedKinks && kink.relatedKinks.length > 0 && (
                <div className="glass-card p-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-5 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Explore Related
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {kink.relatedKinks.slice(0, 5).map(rel => (
                      <Link
                        key={rel.id}
                        to={`/kink/${rel.id}`}
                        className="block p-3 rounded-lg bg-dark-800/50 hover:bg-dark-700 border border-dark-700/50 hover:border-red-500/30 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-200 group-hover:text-white">{rel.name}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-dark-900/50 text-gray-500">{rel.category || 'Other'}</span>
                        </div>
                        {rel.connectionReason && (
                          <div className="text-xs text-gray-500 mt-1 line-clamp-1">{rel.connectionReason}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                  {kink.relatedKinks.length > 5 && (
                    <div className="mt-3 text-center">
                      <span className="text-xs text-gray-500">+{kink.relatedKinks.length - 5} more related</span>
                    </div>
                  )}
                </div>
              )}

              {/* You Might Also Enjoy - Behavioral Overlaps */}


            </div>
          </div>

        </div>
      </div>

      {/* Floating Buttons */}
      <BackToTopButton show={showBackToTop} onClick={handleBackToTop} />
      <MobileTOCButton show={showBackToTop} onClick={() => setMobileTOCOpen(true)} />

      {/* Mobile TOC Drawer */}
      <MobileTOCDrawer
        isOpen={mobileTOCOpen}
        onClose={() => setMobileTOCOpen(false)}
        sections={sections}
        onSelect={(key) => {
          scrollToSection(`section-${key}`, containerRef.current);
          setExpandedSections(prev => ({ ...prev, [key]: true }));
          setMobileTOCOpen(false);
        }}
      />

      {
        showActionEditor && (
          <SexActionEditor
            kink={kink}
            onClose={() => setShowActionEditor(false)}
            onSave={loadKink}
          />
        )
      }
    </div >
  );
};

export default KinkPage;
